








































































































































































































































import { ConfirmModal, ESearch, Pagination, Scrollbar, SvgImage, TwSelect } from '@/app/components';
// import { TButton } from '@/app/components/common';
import { FacetFilterType, useFacetsFilters, useFeatureFlags, useSearch } from '@/app/composable';
import store from '@/app/store';
import { useAlerts } from '@/modules/alert/composable';
import { AlertSourceType } from '@/modules/alert/constants/alert.constants';
import { SearchAPI } from '@/modules/search/api';
import { RefreshIcon } from '@vue-hero-icons/outline';
import { ExclamationIcon } from '@vue-hero-icons/solid';
import { computed, defineComponent, onBeforeUnmount, ref } from '@vue/composition-api';
import { OrbitSpinner } from 'epic-spinners';
import * as R from 'ramda';
import { AssetsAPI } from '../api';
import { AssetOverview } from '../components';
import { useAssetFacets } from '../composable/asset-facets';
import { useAssetFilters } from '../composable/asset-filters';
import { AssetSortingOption, AssetTypeId, StatusCode } from '../constants';

export default defineComponent({
    name: 'Assets',
    metaInfo: {
        title: 'My Assets',
    },
    components: {
        OrbitSpinner,
        AssetOverview,
        SvgImage,
        ConfirmModal,
        // FilterIcon, // only used by the (commented out) filters panel toggle
        Pagination,
        TwSelect,
        Scrollbar,
        ESearch,
        // FacetsFilters, // only used by the (commented out) filters side panel
        // SearchTerms, // only used by the (commented out) searched facet/filter terms
        RefreshIcon,
        // BellIcon,
        // TButton,
        ExclamationIcon,
    },
    setup(props, { root }) {
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');
        const isAnalyticsEnabled = flag('analytics');
        const searchableFields = [
            {
                label: 'title',
                key: 'name',
                textColorClasses: 'text-primary-700',
                backgroundClasses: 'bg-primary-700',
                highlightClasses: 'bg-primary-300',
                widthClasses: 'pl-16 ml-2',
            },
            {
                label: 'description',
                key: 'description',
                textColorClasses: 'text-teal-700',
                backgroundClasses: 'bg-teal-700',
                highlightClasses: 'bg-teal-300',
                widthClasses: 'pl-28 ml-2',
            },
            {
                label: 'tags',
                key: 'metadata.general.tags',
                textColorClasses: 'text-purple-700',
                backgroundClasses: 'bg-purple-700',
                highlightClasses: 'bg-purple-300',
                widthClasses: 'pl-20 ',
            },
        ];
        const openFilters = ref<boolean>(true);
        const pageSize = 40;
        // model assets are no longer supported, so only datasets and results are searched
        const supportedAssetTypeIds = [AssetTypeId.Dataset, AssetTypeId.Result];
        const showDeleteModal = ref(false);
        const toBeDeleted = ref<number | null>(null);
        const facets = ref<Record<string, { value: string; count: number; selected: boolean }[]>>({});
        const assets = ref<any>([]);
        const rejectedItems = ref<number>(0);
        const totalResults = ref<number>(0);
        const user = ref(store.state.auth.user);
        const affectedPipelinesAndQueries = ref<{ pipelines: number; queries: number }>({ pipelines: 0, queries: 0 });

        const { checkAlert, fetchAlerts } = useAlerts(root, [AlertSourceType.Dataset, AlertSourceType.Result]);
        fetchAlerts();

        const refreshProvenanceIds = computed(() => store.state.notificationEngine.refresh.provenanceIds);

        const refresh = computed(
            () =>
                refreshProvenanceIds.value.length &&
                assets.value.find(
                    (asset: any) =>
                        asset.status === StatusCode.Uploading &&
                        asset.value?.provenanceAssetWorkflowIds &&
                        asset.value.provenanceAssetWorkflowIds.some((workflowId: string) =>
                            refreshProvenanceIds.value.includes(workflowId),
                        ),
                ),
        );

        const setSearchQuery = () => {
            // asset type tabs are hidden, all (dataset and result) assets are always shown
            tab.value = 'all';
            delete searchQuery.value.assetTypeId;
            searchQuery.value.facets = reducedAssetFacets;
            searchQuery.value.filters = reducedAssetFilters;
        };

        const search = () => {
            setCommonSearchQueryValues();

            searchQuery.value.query.settings = query.value.settings;
            searchQuery.value.settings.excludeSchema = true;
            searchQuery.value.settings.userId = user.value.id;

            if (user.value.organisationId) {
                searchQuery.value.settings.organisationId = user.value.organisationId;
            }

            setQueryParams();

            exec(
                SearchAPI.facetedSearch({
                    ...searchQuery.value,
                    facets: {
                        ...searchQuery.value.facets,
                        // model assets are no longer supported, only datasets and results are ever shown
                        assetType: selectedAssetTypeIds.value.length
                            ? selectedAssetTypeIds.value
                            : supportedAssetTypeIds,
                    },
                }),
            ).then((res: any) => {
                assets.value = res.data?.results;
                facets.value = res.data?.facets;
                rejectedItems.value = res.data?.accessControl?.rejectedItems || 0;
                totalResults.value = res.data?.total;

                // keep only selected statuses
                if (get('reset') && facets.value?.status) {
                    const facetStatus = facets.value.status
                        .filter((statusFacet: any) => statusFacet.selected)
                        .map((statusFacet: any) => statusFacet.value);
                    const statuses = R.clone(searchQuery.value.facets.status);
                    statuses.forEach((statusFacet: string) => {
                        if (!facetStatus.includes(statusFacet))
                            searchQuery.value.facets.status.splice(
                                searchQuery.value.facets.status.indexOf(statusFacet),
                                1,
                            );
                    });
                    root.$router.replace({
                        name: 'assets',
                        query: { status: searchQuery.value.facets.status.join(',') },
                    });
                }
            });
        };

        const {
            exec,
            loading,
            error,
            get,
            textNumberFilters,
            page,
            sortBy,
            tab,
            query,
            searchQuery,
            updateCurrentPage,
            facetChanged,
            filterChanged,
            sortByChanged,
            clearFilters,
            setCommonSearchQueryValues,
            setQueryParams,
            unsubscribeSearch,
            sortingFields,
        } = useSearch(root, 'assets', pageSize, AssetSortingOption, 'all', setSearchQuery, search, ['spatialCoverage']);

        onBeforeUnmount(() => {
            unsubscribeSearch.value ? unsubscribeSearch.value() : undefined;
            if (refresh.value) store.dispatch.notificationEngine.clearRefreshProvenanceIds();
        });

        const { assetFacets, reducedAssetFacets } = useAssetFacets(get);

        const { assetFilters, reducedAssetFilters } = useAssetFilters(get);

        const { calculateSearchedFacets, calculateSearchedFilters, defaultValue, getValueLabel } = useFacetsFilters([
            'spatialCoverage',
        ]);

        const assetTypeSelected = computed(() => (rejectedItems.value === 1 ? 'asset' : 'assets'));

        const facetsInfo = computed(() => assetFacets.value);

        const filtersInfo = computed(() => assetFilters.value);

        const allOption = { value: null, label: 'All' };

        /**
         * The options of a checkbox facet, prepended with an "All" option that clears the facet
         * @param facetKey The checkbox facet key
         */
        const singleFacetOptions = (facetKey: string) =>
            computed(() => [
                allOption,
                ...(facets.value[facetKey] ?? []).map((option: { value: string }) => ({
                    value: option.value,
                    label: getValueLabel(facetsInfo.value[facetKey], option.value),
                })),
            ]);

        /**
         * The single active value of a checkbox facet, null when the facet is not applied
         * @param facetKey The checkbox facet key
         */
        const singleFacetValue = (facetKey: string) => computed(() => searchQuery.value.facets[facetKey]?.[0] ?? null);

        /**
         * The selected asset types, without any (no longer supported) model asset type that
         * may still be lingering in the query parameters
         */
        const selectedAssetTypeIds = computed(() =>
            (searchQuery.value.facets.assetType ?? []).filter((value: any) =>
                supportedAssetTypeIds.includes(Number(value)),
            ),
        );

        const statusOptions = singleFacetOptions('status');
        const allAssetTypeOptions = singleFacetOptions('assetType');

        /** The asset type options, without the (no longer supported) model asset type */
        const assetTypeOptions = computed(() =>
            allAssetTypeOptions.value.filter(
                (option: { value: any }) => Number(option.value) !== AssetTypeId.Model,
            ),
        );
        const statusValue = singleFacetValue('status');
        const assetTypeValue = computed(() => selectedAssetTypeIds.value[0] ?? null);

        /**
         * Applies a checkbox facet as a single-choice one. Any previously selected value is removed
         * in place (without searching) and the new one is toggled through facetChanged, so that the
         * query parameters are updated and the search is executed exactly once.
         * @param facetKey The checkbox facet key
         * @param value The newly selected value, null when "All" is selected
         */
        const singleFacetChanged = (facetKey: string, value: any) => {
            const current = searchQuery.value.facets[facetKey] ?? [];
            if (current.length === 1 && current[0] === value) return;

            if (R.isNil(value)) {
                if (!current.length) return;
                // keep a single value around so that toggling it off empties the facet
                const remaining = current[current.length - 1];
                current.splice(0, current.length - 1);
                facetChanged(facetKey, remaining, FacetFilterType.Checkbox);
                return;
            }

            current.splice(0, current.length);
            facetChanged(facetKey, value, FacetFilterType.Checkbox);
        };

        const currentSearchedFacetsFilters = computed(() =>
            calculateSearchedFacets(searchQuery.value.facets, facetsInfo.value).concat(
                calculateSearchedFilters(searchQuery.value.filters, filtersInfo.value),
            ),
        );

        const edit = (id: number) => {
            root.$router.push({ name: 'assets:edit', params: { id: `${id}` } });
        };

        const view = (id: number) => {
            root.$router.push({ name: 'assets:view', params: { id: `${id}` } });
        };

        const navigate = (provenanceId: string, assetTypeId: number) => {
            root.$router.push({
                name: assetTypeId === AssetTypeId.Dataset ? 'data-checkin-jobs:edit' : 'workflow-designer:edit',
                params: { id: provenanceId, backTo: 'assets' },
            });
        };

        const destroy = async () => {
            if (toBeDeleted.value) {
                exec(AssetsAPI.deleteAsset(toBeDeleted.value))
                    .then(async (res: any) => {
                        const asset = assets.value.find((item: any) => item.id === toBeDeleted.value);
                        root.$set(asset, 'status', res.data.status);
                        root.$set(asset, 'updatedAt', res.data.updatedAt);
                        toBeDeleted.value = null;

                        (root as any).$toastr.s('Asset deleted successfuly', 'Success');
                        page.value = 1;
                        updateCurrentPage();
                    })
                    .catch(() => {
                        (root as any).$toastr.e('Deleting Asset failed', 'Error');
                    });
            }
        };

        const confirmationDeleteMessage = computed(() => {
            let message =
                'Deleting this asset will permanently remove all its data yet its metadata will remain for provenance reasons.';
            if (affectedPipelinesAndQueries.value.pipelines > 0 || affectedPipelinesAndQueries.value.queries > 0) {
                message = message + ' There ';
                message =
                    message +
                    `${
                        affectedPipelinesAndQueries.value.pipelines + affectedPipelinesAndQueries.value.queries > 1
                            ? 'are '
                            : 'is '
                    }`;
                if (affectedPipelinesAndQueries.value.pipelines > 0) {
                    message =
                        message +
                        `<span class= 'font-bold'>${affectedPipelinesAndQueries.value.pipelines}</span> Analytics Pipeline(s) `;
                }
                if (affectedPipelinesAndQueries.value.pipelines > 0 && affectedPipelinesAndQueries.value.queries > 0) {
                    message = message + 'and ';
                }
                if (affectedPipelinesAndQueries.value.queries > 0) {
                    message =
                        message +
                        `<span class= 'font-bold'>${affectedPipelinesAndQueries.value.queries}</span> Retrieval Querie(s) `;
                }
                message =
                    message +
                    `that ${
                        affectedPipelinesAndQueries.value.pipelines > 1 || affectedPipelinesAndQueries.value.queries > 1
                            ? 'are'
                            : 'is'
                    } currently using this asset and will be affected.`;
            }
            message =
                message +
                ' Please note that this action cannot be undone! Are you sure you still want to delete this asset?';
            return message;
        });

        const confirmDelete = (id: any) => {
            toBeDeleted.value = id;
            exec(AssetsAPI.getAffectedPipelinesQueries(id))
                .then((res: any) => {
                    affectedPipelinesAndQueries.value = res.data;
                    showDeleteModal.value = true;
                })
                .catch(() => {
                    (root as any).$toastr.e('Deleting Asset failed', 'Error');
                });
        };

        textNumberFilters.value = {
            relatedPipeline: defaultValue(get, 'relatedPipeline', false),
            assetOrigin: defaultValue(get, 'assetOrigin', false),
            derivativeAssets: defaultValue(get, 'derivativeAssets', false),
        };
        setSearchQuery();
        search();

        const archiveAsset = (id: number) => {
            exec(AssetsAPI.archiveAsset(id))
                .then(() => {
                    (root as any).$toastr.s('Model Asset archived successfuly', 'Success');
                    search();
                })
                .catch(() => {
                    (root as any).$toastr.e('Archiving Model Asset failed', 'Error');
                });
        };

        const restoreAsset = (id: number) => {
            exec(AssetsAPI.restoreArchivedAsset(id))
                .then(() => {
                    (root as any).$toastr.s('Model Asset restored successfuly', 'Success');
                    search();
                })
                .catch(() => {
                    (root as any).$toastr.e('Restoring Model Asset failed', 'Error');
                });
        };

        const modalConfirmed = () => {
            showDeleteModal.value = false;
            destroy();
        };

        const cancelAction = () => {
            showDeleteModal.value = false;
            toBeDeleted.value = null;
        };

        const refreshPage = () => {
            search();
            store.dispatch.notificationEngine.clearRefreshProvenanceIds();
        };

        return {
            loading,
            error,
            assets,
            edit,
            view,
            destroy,
            confirmDelete,
            showDeleteModal,
            tab,
            statusOptions,
            assetTypeOptions,
            statusValue,
            assetTypeValue,
            singleFacetChanged,
            archiveAsset,
            restoreAsset,
            modalConfirmed,
            cancelAction,
            pageSize,
            totalResults,
            updateCurrentPage,
            openFilters,
            navigate,
            facets,
            facetChanged,
            searchQuery,
            sortingFields,
            sortByChanged,
            query,
            sortBy,
            page,
            rejectedItems,
            assetTypeSelected,
            filterChanged,
            clearFilters,
            facetsInfo,
            filtersInfo,
            currentSearchedFacetsFilters,
            textNumberFilters,
            refresh,
            refreshPage,
            affectedPipelinesAndQueries,
            confirmationDeleteMessage,
            checkAlert,
            searchableFields,
            isAlertsEnabled,
            isAnalyticsEnabled,
        };
    },
});
